import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Actions from "./store/actions";
import Navigation from "./Navigation";
import { Auth, Hub } from "aws-amplify";
import { Authenticator } from "@aws-amplify/ui-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "@aws-amplify/ui-react/styles.css";
import "./assets/sass/style.sass";
import "bootstrap/dist/css/bootstrap.min.css";
import { RingLoader } from "react-spinners";
import Watermark from "./components/Watermark";
import { useNavigate, useLocation } from "react-router-dom";
import ChatAssistant from "./chat/ChatAssistant";

const AUTH_PATHS = [
  "/login",
  "/login-copy",
  "/signup",
  "/register",
  "/confirm",
];

function isAuthPath(pathname) {
  return AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export default function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsloading] = useState(true);
  const isHandlingAuthFailureRef = useRef(false);
  const [loadingStatus, loadingText] = useSelector((state) => {
    return [state.helper.isLoading, state.helper.loadingText];
  });
  const [authChecked, setAuthChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cognitoUser] = useSelector((state) => [state.auth.cognito]);

  if (process.env.REACT_APP_BUILD_ENV === 'prod') {
    console.log = () => {}
    console.error = () => {}
    console.debug = () => {}
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        if (!cancelled) {
          setIsLoggedIn(!!user);
          if (user) dispatch(Actions.authCurrentUser(user));
        }
      } catch {
        if (!cancelled) setIsLoggedIn(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  useEffect(() => {
    if (cognitoUser) setIsLoggedIn(true);
  }, [cognitoUser]);

  const forceLogoutToLogin = async () => {
    if (isHandlingAuthFailureRef.current) return;
    isHandlingAuthFailureRef.current = true;
    try {
      await Auth.signOut().catch(() => {});
    } finally {
      dispatch({ type: "USER_LOGOUT" });
      setIsLoggedIn(false);
      navigate("/login", { replace: true });
      isHandlingAuthFailureRef.current = false;
    }
  };

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      const event = payload?.event;
      if (event === "tokenRefresh_failure") {
        forceLogoutToLogin();
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      const message = reason?.message || reason?.toString?.() || "";
      const isNotAuthorized =
        reason?.name === "NotAuthorizedException" ||
        reason?.errorType === "NotAuthorizedException" ||
        message.includes("NotAuthorizedException") ||
        message.includes("No current user");
      if (isNotAuthorized) {
        forceLogoutToLogin();
      }
    };
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    setIsloading(loadingStatus);
  }, [loadingStatus, isLoading]);

  const onAuthPage = isAuthPath(location.pathname);
  const shouldShowChatAssistant = authChecked && !(onAuthPage && !isLoggedIn);

  return (
    <main>
      <Watermark />
      {isLoading ? (
        <div className="loaderStyling">
          <RingLoader color={"#10A0DE"} size={100} loading={isLoading} />
          {loadingText && <div className="loader-message">{loadingText}</div>}
        </div>
      ) : null}
      <Navigation />
      <ToastContainer />
      {shouldShowChatAssistant && <ChatAssistant />}
    </main>
  );
}
